import Button from '@/shared/Button'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'

import AddCircle from '@mui/icons-material/AddCircle'

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import ConfirmDialog from '@/shared/ConfirmDialog'
import * as Yup from 'yup'
import CreateRegisteredUserCard from './CreateRegisteredUserCard'

import get from 'lodash/get'

const initialList = [{ key: 'user0', id: 0 }]

const CreateRegisteredUserDialog = ({ dispatch, userGroup, onClose, onComplete, ...props }) => {
  const [createList, setCreateList] = useState(initialList)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const itemRefs = useRef([])

  const { t } = useTranslation()

  const handleClose = () => {
    setCreateList(initialList)
    itemRefs.current = []
    onClose?.()
  }

  Yup.addMethod(Yup.string, 'duplicateEmail', function testStartDate(errorMessage, index) {
    return this.test('test-duplicate-email', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context
      const { email } = parent

      if (
        itemRefs.current.filter(
          (compare, compareIndex) =>
            index !== compareIndex && compare.form.getValues().email === email,
        ).length > 0
      ) {
        return createError({ path, message: errorMessage })
      }
      return true
    })
  })

  return (
    <ConfirmDialog
      {...props}
      onSubmit={async () => {
        // eslint-disable-next-line no-restricted-syntax
        for (const r of itemRefs.current) {
          // eslint-disable-next-line no-await-in-loop
          await r.form.trigger()
        }

        const hasError = itemRefs.current?.filter?.(r => !r.form.formState.isValid).length > 0

        if (!hasError) {
          setIsSubmitting(true)

          let hasFailed = false
          const createdUsers = []
          // eslint-disable-next-line no-restricted-syntax
          for (const r of itemRefs.current) {
            // eslint-disable-next-line no-await-in-loop
            const result = await r?.submit?.()

            if (result) {
              createdUsers.push(result)
            } else {
              hasFailed = true
            }
          }

          if (!hasFailed) {
            const postData = createdUsers.map(s => s.userId)

            if (userGroup) {
              dispatch({
                type: 'userGroup/addUsersToUserGroup',
                payload: {
                  userList: postData,
                  ...(userGroup ? { userGroupId: userGroup.id } : {}),
                },
                successCallback: () => {
                  dispatch({
                    type: 'app/notify',
                    payload: { message: t('messages.user_created'), severity: 'success' },
                  })

                  onComplete?.()
                  handleClose?.()
                  setIsSubmitting(false)
                },
              })
            } else {
              dispatch({
                type: 'app/notify',
                payload: { message: t('messages.user_created'), severity: 'success' },
              })

              onComplete?.()
              handleClose?.()
              setIsSubmitting(false)
            }
          } else {
            setIsSubmitting(false)
          }
        }
      }}
      onClose={handleClose}
      loading={isSubmitting}
      title={t('general.add_registered_user')}
    >
      {userGroup && (
        <Typography fontWeight="bold" display="flex" mb={1}>
          {`${t('general.user_group_name')}: `}
          <Typography fontWeight="bold" color="primary" ml={1}>{`${get(
            userGroup,
            'name',
          )}`}</Typography>
        </Typography>
      )}
      {createList.map((obj, i) => (
        <div key={obj.key}>
          <CreateRegisteredUserCard
            ref={ref => {
              if (ref) {
                itemRefs.current[i] = ref
              }
            }}
            dispatch={dispatch}
            userGroup={userGroup}
            index={i}
            {...obj}
            showRemove={createList.length > 1}
            onRemove={id => {
              itemRefs.current?.splice?.(i, 1)
              const newList = createList.filter(o => o.id !== id)
              setCreateList(createList.filter(o => o.id !== id))

              if (newList.length === 0) {
                handleClose()
              }
            }}
            loading={isSubmitting}
          />
          <Divider
            sx={{
              bgcolor: theme => theme.palette.primary.main,
              height: '0px',
              background: 'transparent',
              borderTop: '1px dashed #FF6700',
              marginTop: '50px',
              marginBottom: '20px',
            }}
            orientation="vertical"
          />
        </div>
      ))}

      {createList.length < 5 ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            sx={{ color: '#FF6700' }}
            color={'white'}
            disabled={isSubmitting}
            endIcon={<AddCircle sx={{ color: '#FF6700' }} />}
            onClick={() => {
              const latestId = createList[createList.length - 1].id + 1
              setCreateList([...createList, { key: `user${latestId}`, id: latestId }])
            }}
          >
            {t('general.add_next')}
          </Button>
        </Box>
      ) : (
        <Typography color="error">{t('validation.user_create_max')}</Typography>
      )}
    </ConfirmDialog>
  )
}

export default connect(() => ({}))(CreateRegisteredUserDialog)
